import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { rccColors } from "../colors";
import { appService } from "../../App/app.service";
import D3HorizontalDualBar from "./D3HorizontalDualBar";
import RccCard from "../RccCard";
import { fetchDefaultTeamFilter, fetchFirstLevelTeamNames } from "../rccUtils";

/**
 * Displays pipeline coverage multiplier and won percentage by team.
 * Inputs: Redux state (PipelineCoverageCFQ, user, company).
 * Outputs: Horizontal dual bar chart.
 */
function PipelineCoverage({ title, redirectToDashboard }) {
  const dispatch = useDispatch();
  const fetchingRef = useRef(false);
  const [firstLevelTeamNames, setFirstLevelTeamNames] = useState(null);
  const [defaultTeamFilter, setDefaultTeamFilter] = useState(null);
  const [appliedFilters, setAppliedFilters] = useState([]);

  // Get data from Redux store
  const pipelineCoverageData = useSelector(
    (state) => state.app.PipelineCoverageCFQ || "",
  );
  const currencySymbol = useSelector((state) => state.app.user?.Currency);

  // Check if data is loading
  const isLoading = pipelineCoverageData === "";

  // Fetch first-level team names from performance scorecard filters
  useEffect(() => {
    fetchFirstLevelTeamNames().then((names) =>
      setFirstLevelTeamNames(Array.isArray(names) ? names : [])
    );
    fetchDefaultTeamFilter().then((teams) =>
      setDefaultTeamFilter(Array.isArray(teams) ? teams : [])
    );
  }, []);

  // Fetch data when filters change
  const lastFiltersRef = useRef();
  const hasFirstLevelTeams =
    Array.isArray(firstLevelTeamNames) && firstLevelTeamNames.length > 0;
  const isTeamFiltersReady =
    firstLevelTeamNames !== null && defaultTeamFilter !== null;

  const resolvedFilters = useMemo(() => {
    if (hasFirstLevelTeams) {
      return [];
    }

    if (Array.isArray(defaultTeamFilter) && defaultTeamFilter.length > 0) {
      return [{ name: "team", value: defaultTeamFilter, string: true }];
    }

    return [];
  }, [hasFirstLevelTeams, defaultTeamFilter]);

  useEffect(() => {
    setAppliedFilters(resolvedFilters);
  }, [resolvedFilters]);

  useEffect(() => {
    if (!isTeamFiltersReady) return;

    // Skip if already fetching
    if (fetchingRef.current) return;

    // Check if filters actually changed or if data is missing
    const filtersStr = JSON.stringify(resolvedFilters);
    if (lastFiltersRef.current === filtersStr && pipelineCoverageData !== "")
      return;

    fetchingRef.current = true;
    lastFiltersRef.current = filtersStr;

    dispatch({ type: "get_generic_pipeline_coverage_data_CFQ_request" });
    appService.getGenericPipelineCoverageData(resolvedFilters, "CFQ").then(
      (json) => {
        fetchingRef.current = false;
        dispatch({
          type: "get_generic_pipeline_coverage_data_CFQ_success",
          json,
        });
      },
      (error) => {
        fetchingRef.current = false;
        dispatch({
          type: "get_generic_pipeline_coverage_data_CFQ_failure",
          error: typeof error === "object" ? "Something went wrong" : error,
        });
      },
    );
  }, [pipelineCoverageData, resolvedFilters, isTeamFiltersReady, dispatch]);

  // Transform data for chart
  const chartData = useMemo(() => {
    if (isLoading || !pipelineCoverageData || !pipelineCoverageData.data) {
      return [];
    }

    // Filter out company/team aggregate rows
    let teams = pipelineCoverageData.data.filter((item) => {
      const name = String(item.name || item.team || "")
        .trim()
        .toLowerCase();
      return !["company", "team", "other"].includes(name);
    });

    // Filter to only first-level teams if we have the team names
    if (hasFirstLevelTeams) {
      teams = teams.filter((team) => {
        const teamName = team.name || team.team || "";
        return firstLevelTeamNames.includes(teamName);
      });

      // Sort teams according to the order they appear in firstLevelTeamNames
      teams.sort((a, b) => {
        const teamNameA = a.name || a.team || "";
        const teamNameB = b.name || b.team || "";
        const indexA = firstLevelTeamNames.indexOf(teamNameA);
        const indexB = firstLevelTeamNames.indexOf(teamNameB);
        // If team not found in firstLevelTeamNames, put it at the end
        if (indexA === -1) return 1;
        if (indexB === -1) return -1;
        return indexA - indexB;
      });
    }

    return teams.map((team) => {
      // Extract values - based on API response structure
      // quotaRemaining is the "Quota Remaining" for next quarter
      const quotaRemaining =
        team.remainingCurrentQuarter ||
        team.quotaLeft ||
        team.quotaRemaining ||
        0;

      // qualifiedPipeline is the "Qualified Pipeline" ACV
      const qualifiedPipeline = team.acv || team.qualifiedPipeline || 0;

      // won is the "Won" ACV (may need to be calculated or come from API)
      // If not directly available, we might need to calculate from other fields
      const won = team.won || team.wonAcv || 0;

      // Calculate percentage (Won as % of Quota Remaining)
      const percentage =
        quotaRemaining > 0 ? Math.round((won / quotaRemaining) * 100) : 0;

      // Calculate multiplier (Total Pipeline / Quota Remaining)
      const totalPipeline = won + qualifiedPipeline;
      const multiplier =
        quotaRemaining > 0
          ? (totalPipeline / quotaRemaining).toFixed(1)
          : "0.0";

      return {
        team: team.name || team.team || "Unknown",
        quotaRemaining,
        won,
        qualifiedPipeline,
        percentage,
        multiplier,
      };
    });
  }, [pipelineCoverageData, isLoading, firstLevelTeamNames]);

  const handleRedirect = () => {
    console.log("Redirecting to Pipeline Coverage Dashboard...");
  };

  const handleZoom = () => {
    console.log("Zooming Pipeline Coverage Chart...");
  };

  return (
    <RccCard
      title={title || "Qualified Pipeline Coverage (CFQ)"}
      isLoading={isLoading}
      redirectToDashboard={redirectToDashboard || "Pipeline Coverage Analysis"}
      onZoomClick={handleZoom}
      appliedFilters={appliedFilters}
      focusVariant={"current"}
    >
      <div
        style={{
          flex: 1,
          width: "100%",
          minHeight: "300px",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {chartData.length > 0 ? (
          <D3HorizontalDualBar
            data={chartData}
            options={{
              currencySymbol,
              showValueLabels: true,
              showLegend: true,
            }}
          />
        ) : (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flex: 1, // Fills the wrapper
              color: rccColors.neutral[400],
            }}
          >
            No data available
          </div>
        )}
      </div>
    </RccCard>
  );
}

export default PipelineCoverage;
